import React, { useCallback } from 'react'
import {
  initialValuesFragment,
  mapSubmitValues,
  novaPastaValidationSchema,
  novaSubPastaSchema
} from '../utils/functions'
import { t } from '@lingui/core/macro'
import { CRIAR_NOVA_PASTA } from '../data/criar-nova-pasta'
import { useSnackbar } from '@tecsinapse/cortex-react'
import {
  CriarNovaPastaMutationMutation,
  CriarNovaPastaMutationMutationVariables
} from '@/core/types/graphql'
import { SubmitHandler, useForm } from 'react-hook-form'
import { OptionSelect, GrupoUsuariosFavoritoOption } from '@/core/utils/types'
import { zodResolver } from '@hookform/resolvers/zod'
import { CONST_ROOT_FOLDER } from '../utils/constants'
import { useParams } from 'react-router'
import { request } from '../../../core/utils/request'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { getGraphQLErrorMessage } from '@/core/utils/graphql-error'
import { NovaPastaDrawerProps } from '../components/nova-pasta-drawer'

export interface NovaPastaValues {
  nome: string
  idPai: OptionSelect
  isPublico?: { value: boolean; label: string }
  idsGrupoAcesso?: GrupoUsuariosFavoritoOption[]
  idsGrupoAcessoEscrita?: GrupoUsuariosFavoritoOption[]
}

export const useNovaPastaDialog = ({
  pastaAtual,
  onOpen
}: Pick<NovaPastaDrawerProps, 'pastaAtual' | 'onOpen'>) => {
  const { idPasta } = useParams()
  const isRoot = !idPasta
  const client = useQueryClient()

  const { idsGrupoAcesso, idsGrupoAcessoEscrita, isPublico } =
    initialValuesFragment(pastaAtual)

  const { snackbar } = useSnackbar()

  const criarNovaPastaMutation = useMutation({
    mutationKey: ['criarNovaPasta'],
    mutationFn: async ({
      variables
    }: {
      variables: CriarNovaPastaMutationMutationVariables
    }) =>
      await request<
        CriarNovaPastaMutationMutation,
        CriarNovaPastaMutationMutationVariables
      >(CRIAR_NOVA_PASTA, { variables }),
    onSuccess: async () => {
      await client.refetchQueries({ queryKey: ['itemsPorPasta'] })
      await client.refetchQueries({ queryKey: ['pastasPermissaoEscrita'] })
    }
  })

  const form = useForm<NovaPastaValues>({
    values: {
      nome: '',
      idPai: {
        label: pastaAtual.nome,
        value: pastaAtual.id
      },
      ...(isRoot && {
        isPublico,
        idsGrupoAcesso,
        idsGrupoAcessoEscrita
      })
    },
    resolver: zodResolver(
      isRoot ? novaPastaValidationSchema : novaSubPastaSchema
    ),
    mode: 'all'
  })

  const onSubmit: SubmitHandler<NovaPastaValues> = async submitValues => {
    const novaPasta = {
      nome: submitValues.nome,
      idPai:
        submitValues.idPai.value === CONST_ROOT_FOLDER
          ? null
          : submitValues.idPai.value,
      ...mapSubmitValues(submitValues)
    }

    const { nome } = submitValues
    const successMessage = t`Pasta ${nome} criada com sucesso!`

    try {
      await criarNovaPastaMutation.mutateAsync({
        variables: { novaPasta }
      })

      snackbar.show('success', successMessage)

      form.reset()
      onOpen()
    } catch (error) {
      snackbar.show(
        'error',
        getGraphQLErrorMessage(error) ?? t`Não foi possível criar a pasta :(`
      )
    }
  }

  const closeForm = useCallback(() => {
    if (form.formState.isSubmitting) {
      return
    }
    form.reset()
    onOpen()
  }, [form, onOpen])

  return {
    form,
    onSubmit,
    closeForm
  }
}
